import type {
  AddFilesResponse, ApiError, CompleteSubmissionResponse, CreateSubmissionResponse, FileDeclaration, RenewSasResponse,
} from '@onerepair/contract';
import { ERROR_MESSAGES } from '@onerepair/contract';

export class ApiRequestError extends Error {
  constructor(public readonly body: ApiError & Record<string, unknown>, public readonly status: number) {
    super(body.message);
  }
}

const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

async function call<T>(path: string, init: RequestInit & { token?: string } = {}): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json', ...(init.headers as Record<string, string> | undefined) };
  if (init.token) headers.authorization = `Bearer ${init.token}`;
  let res: Response;
  try {
    res = await fetch(base + path, { ...init, headers });
  } catch {
    throw new ApiRequestError({ code: 'INTERNAL', message: ERROR_MESSAGES.INTERNAL }, 0);
  }
  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => ({ code: 'INTERNAL', message: ERROR_MESSAGES.INTERNAL }))) as T & ApiError;
  if (!res.ok) throw new ApiRequestError(body as ApiError & Record<string, unknown>, res.status);
  return body;
}

export const api = {
  create: (files: FileDeclaration[], turnstileToken?: string, website = '') =>
    call<CreateSubmissionResponse>('/submissions', { method: 'POST', body: JSON.stringify({ files, turnstileToken, website }) }),
  addFiles: (id: string, token: string, files: FileDeclaration[]) =>
    call<AddFilesResponse>(`/submissions/${id}/files`, { method: 'POST', token, body: JSON.stringify({ files }) }),
  renewSas: (id: string, token: string, fileId: string) =>
    call<RenewSasResponse>(`/submissions/${id}/files/${fileId}/sas`, { method: 'POST', token }),
  removeFile: (id: string, token: string, fileId: string) =>
    call<{ ok: true }>(`/submissions/${id}/files/${fileId}`, { method: 'DELETE', token }),
  complete: (id: string, token: string, email: string) =>
    call<CompleteSubmissionResponse>(`/submissions/${id}/complete`, { method: 'POST', token, body: JSON.stringify({ email, locale: navigator.language }) }),
};
